import { Component, Input } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { PickupLocationModalComponent } from '../modals/pickup-location-modal/pickup-location-modal.component';
import {
  LoaderService,
  OrderService,
  ObservableService,
  CONSTANT,
  AppSettingService,
  CommonService,
} from '@bst-frontend/shared';
import { finalize, forkJoin, map, of, Subscription } from 'rxjs';
import { AddEstimateModalComponent } from '../modals/add-estimate-modal/add-estimate-modal.component';
import { AssignTruckModalComponent } from '../modals/assign-truck-modal/assign-truck-modal.component';
import { AssignDriverModalComponent } from '../modals/assign-driver-modal/assign-driver-modal.component';
import { VehicleAssignModalComponent } from '../modals/vehicle-assign-modal/vehicle-assign-modal.component';
import { ReadyDispatchModalComponent } from '../modals/ready-dispatch-modal/ready-dispatch-modal.component';
import { DeliverModalComponent } from '../modals/deliver-modal/deliver-modal.component';
import { ConfirmPaymentModalComponent } from '../modals/confirm-payment-modal/confirm-payment-modal.component';
import {
  CommonModalComponent,
  DeleteConfirmationComponent,
} from '@bst-frontend/common-ui';
import { DeliveryLocationModalComponent } from '../modals/delivery-location-modal/delivery-location-modal.component';
import { PartLoadPickupLocationModalComponent } from '../modals/part-load-pickup-location-modal/part-load-pickup-location-modal.component';
import { updateFareModalComponent } from '../modals/update-fare-modal/update-fare-modal.component';

type Address = { _id: string; addressId?: string; [k: string]: any };

@Component({
  selector: 'bst-frontend-right-panel',
  templateUrl: './right-panel.component.html',
  styleUrls: ['./right-panel.component.css'],
})
export class RightPanelComponent {
  @Input() orderData: any;
  @Input() isPickupLocation = false;
  private userAddresses: Address[] | null = null;
  private companyAddresses: Address[] | null = null;
  private productData: any = null;
  orderStateCode = CONSTANT.orderStateCode;
  subscriptions: Subscription[] = [];

  orderType = {
    partLoadOffice: 'part-load-office',
    partLoadDoor: 'part-load-door',
    fullLoad: 'full_load',
  };

  showOrderState = [
    'inProcess',
    'inTransit',
    'delivered',
    'invoiceUploaded',
    'paid',
  ];
  config: any;
  oderType = ['part-load-door', 'part-load-warehouse'];

  constructor(
    private modalService: NgbModal,
    private orderService: OrderService,
    private loader: LoaderService,
    private observableService: ObservableService,
    private appSettingService: AppSettingService,
    private commonService: CommonService
  ) {}

  ngOnInit() {
    if (this.orderData.type !== 'full-load') {
      this.getSettingDetails();
    }
    this.orderData.subOrders.forEach((sub: any) => {
      sub.deliveries?.forEach((delivery: any) => {
        delivery.items.forEach((item: any) => {
          item.planned.showWeight = this.commonService.getChargeWeight(
            item,
            sub?.productDifferences
          );
          item.planned.showQTY = this.commonService.getQty(
            item,
            sub?.productDifferences
          );
        });
      });
      sub.pickup?.items?.forEach((item: any) => {
        item.planned.showWeight = this.commonService.getChargeWeight(
          item,
          sub?.productDifferences
        );
        item.planned.showQTY = this.commonService.getQty(
          item,
          sub?.productDifferences
        );
      });
    });
    const sub = this.observableService.editFare$.subscribe(() =>
      this.openAddEstimateModal()
    );
    this.subscriptions.push(sub);
  }

  getSettingDetails() {
    this.appSettingService.getAppSetting().subscribe({
      next: (resp) => {
        this.config = resp.data?.settings?.partLoad;
      },
    });
  }

  private mapAddresses = (docs: Address[]): Address[] =>
    docs.map((a) => ({ ...a, addressId: a._id }));

  private fetchUserAddresses() {
    const params = { userId: this.orderData.orderBy._id, isCompany: false };
    return this.orderService
      .getUserAddresses(params)
      .pipe(map((res: any) => this.mapAddresses(res.data.docs)));
  }

  private fetchCompanyAddresses() {
    return this.orderService
      .getUserAddresses({ isCompany: true })
      .pipe(map((res: any) => this.mapAddresses(res.data.docs)));
  }

  getAddresses(): void {
    const needUser = !this.userAddresses;
    const needCompany = !this.companyAddresses;
    const needProducts = !this.productData;

    // if both already cached, just apply and exit
    if (!needUser && !needCompany) {
      this.applyAddressPools();
      return;
    }

    this.loader.show();

    forkJoin({
      user: needUser ? this.fetchUserAddresses() : of(this.userAddresses!),
      company: needCompany
        ? this.fetchCompanyAddresses()
        : of(this.companyAddresses!),
      products: needProducts
        ? this.orderService
            .getProducts()
            .pipe(map((res: any) => res.data || []))
        : of(this.productData!),
    })
      .pipe(finalize(() => this.loader.hide()))
      .subscribe({
        next: ({ user, company, products }) => {
          this.userAddresses = user;
          this.companyAddresses = company;
          this.productData = products;
          this.applyAddressPools();
        },
        error: (err) => {
          console.error('Failed to load addresses', err);
          // optionally: this.toastr.error('Unable to load addresses');
        },
      });
  }
  private applyAddressPools(): void {
    const useCompany = this.orderData?.type === this.orderType.partLoadOffice; // <-- your condition
    const pool = useCompany ? this.companyAddresses! : this.userAddresses!;
  }

  totalWeight(d: any) {
    return d.items.reduce(
      (s: any, x: any) => s + (Number(x.planned?.weight) || 0),
      0
    );
  }
  totalQty(d: any) {
    return d.items.reduce(
      (s: any, x: any) => s + (Number(x.planned?.qty) || 0),
      0
    );
  }

  openPickupModal(
    existingPickupPayload: any = null,
    index: null | number = null
  ) {
    const haveUserAddresses = !!this.userAddresses?.length;
    const haveCompanyAddresses = !!this.companyAddresses?.length;

    if (!haveCompanyAddresses) {
      this.loader.show(); // show loader while fetching
      this.getAddresses();

      const checkLoaded = setInterval(() => {
        if (this.companyAddresses?.length) {
          clearInterval(checkLoaded);
          this.loader.hide();
          this._openPickupModal(existingPickupPayload, index);
        }
      }, 300);
      return;
    }
    this._openPickupModal(existingPickupPayload, index);
  }

  private _openPickupModal(
    existingPickupPayload: any = null,
    index: null | number = null
  ) {
    const modalRef = this.modalService.open(
      this.orderData?.type === 'full-load'
        ? PickupLocationModalComponent
        : PartLoadPickupLocationModalComponent,
      {
        centered: true,
        size: 'lg',
        backdrop: 'static',
      }
    );

    modalRef.componentInstance.userAddresses = this.userAddresses;
    modalRef.componentInstance.companyAddresses = this.companyAddresses;
    modalRef.componentInstance.productData = this.productData;
    modalRef.componentInstance.orderId = this.orderData?._id;
    modalRef.componentInstance.orderType = this.orderData?.type;
    modalRef.componentInstance.dataToEdit = existingPickupPayload;
    modalRef.componentInstance.config = this.config;
    modalRef.componentInstance.index = index;
    modalRef.componentInstance.orderData = this.orderData;

    modalRef.result
      .then((result) => {
        if (result === 'saved') {
          console.log('Pickup saved!');
        }
        this.observableService.send({ refresh: true });
      })
      .catch(() => console.log('Modal dismissed'));
  }

  openDeliveryModal(existingPickupPayload: any = null) {
    const haveCompanyAddresses = !!this.companyAddresses?.length;

    if (!haveCompanyAddresses) {
      this.loader.show(); // show loader while fetching
      this.getAddresses();

      const checkLoaded = setInterval(() => {
        if (this.companyAddresses?.length) {
          clearInterval(checkLoaded);
          this.loader.hide();
          this._openDeliveryModal(existingPickupPayload);
        }
      }, 300);
      return;
    }
    this._openDeliveryModal(existingPickupPayload);
  }

  private _openDeliveryModal(existingPickupPayload: any = null) {
    const modalRef = this.modalService.open(DeliveryLocationModalComponent, {
      centered: true,
      size: 'lg',
      backdrop: 'static',
    });

    modalRef.componentInstance.userAddresses = this.userAddresses;
    modalRef.componentInstance.companyAddresses = this.companyAddresses;
    modalRef.componentInstance.productData = this.productData;
    modalRef.componentInstance.orderId = this.orderData?._id;
    modalRef.componentInstance.orderType = this.orderData?.type;
    modalRef.componentInstance.dataToEdit = existingPickupPayload;

    modalRef.result
      .then((result) => {
        if (result === 'saved') {
          console.log('Delivery saved!');
        }
        this.observableService.send({ refresh: true });
      })
      .catch(() => console.log('Modal dismissed'));
  }

  vehicleThereModal(item: any) {
    const modalRef = this.modalService.open(DeleteConfirmationComponent, {
      centered: true,
      backdrop: 'static',
    });

    modalRef.componentInstance.title = 'Are you sure vehicle there ?';
    modalRef.componentInstance.message = `This action will confirm that vehicle ${item?.truckAssignment?.tempoNo} has reached its destination. Please verify before proceeding.`;
    modalRef.componentInstance.confirmText = 'Yes, Confirm';
    modalRef.componentInstance.cancelText = 'Cancel';

    const payload = {
      notes: 'Vehicle There.',
    };

    modalRef.result
      .then((result) => {
        if (result === 'confirm') {
          this.loader.show();

          this.orderService
            .updateSubOrder(
              CONSTANT.orderStatus['VEHICLE-THERE'],
              item._id,
              payload
            )
            .pipe(finalize(() => this.loader.hide()))
            .subscribe({
              next: (res: any) => {
                if (res.status === 'success') {
                  this.observableService.send({ refresh: true });
                }
              },
              error: (err) => {
                console.error('Delete error', err);
              },
            });
        }
      })
      .catch(() => {
        console.log('errror');
      });
  }

  truckReachedModal(item: any) {
    const modalRef = this.modalService.open(DeleteConfirmationComponent, {
      centered: true,
      backdrop: 'static',
    });

    modalRef.componentInstance.title = 'Are you sure truck reached ?';
    modalRef.componentInstance.message = `This action will confirm that truck has reached. Please verify before proceeding.`;
    modalRef.componentInstance.confirmText = 'Yes, Confirm';
    modalRef.componentInstance.cancelText = 'Cancel';

    const payload = {
      notes: 'Truck Reached.',
    };

    modalRef.result
      .then((result) => {
        if (result === 'confirm') {
          this.loader.show();

          this.orderService
            .updateSubOrder(
              CONSTANT.orderStatus['LR-ARRIVED'],
              item._id,
              payload
            )
            .pipe(finalize(() => this.loader.hide()))
            .subscribe({
              next: (res: any) => {
                if (res.status === 'success') {
                  this.observableService.send({ refresh: true });
                }
              },
              error: (err) => {
                console.error('Delete error', err);
              },
            });
        }
      })
      .catch(() => {
        console.log('errror');
      });
  }

  deleteSubOrder(subOrderId: string) {
    const modalRef = this.modalService.open(DeleteConfirmationComponent, {
      centered: true,
      backdrop: 'static',
    });

    modalRef.componentInstance.title = 'Delete Product';
    modalRef.componentInstance.message = `Are you sure you want to delete?`;
    modalRef.componentInstance.confirmText = 'Yes, Delete';
    modalRef.componentInstance.cancelText = 'No';

    modalRef.result
      .then((result) => {
        if (result === 'confirm') {
          this.loader.show();
          this.orderService
            .deleteOrderItemV2(subOrderId)
            .pipe(finalize(() => this.loader.hide()))
            .subscribe({
              next: (res: any) => {
                if (res.status === 'success') {
                  this.observableService.send({ refresh: true });
                }
              },
              error: (err) => {
                console.error('Delete error', err);
              },
            });
        }
      })
      .catch(() => {
        console.log('errror');
      });
  }
  openAddEstimateModal() {
    const modalRef = this.modalService.open(
      this.orderData?.type === 'full-load'
        ? AddEstimateModalComponent
        : updateFareModalComponent,
      {
        centered: true,
        size: 'lg', // options: sm, lg, xl
        backdrop: 'static', // optional: prevent closing by click outside
      }
    );

    modalRef.componentInstance.orderData = this.orderData;

    modalRef.result
      .then((result) => {
        if (result === 'saved') {
          console.log('estimate added!');
        }
      })
      .catch(() => {
        console.log('errror');
      });
  }

  openAssignTruckModal(
    mode: 'create' | 'edit' | 'view' = 'create',
    subOrder: any
  ) {
    const modalRef = this.modalService.open(AssignTruckModalComponent, {
      centered: true,
      size: 'lg', // options: sm, lg, xl
      backdrop: 'static', // optional: prevent closing by click outside
    });

    modalRef.componentInstance.orderData = this.orderData;
    modalRef.componentInstance.subOrder = subOrder;
    if (mode === 'edit' || mode === 'view') {
      modalRef.componentInstance.initialData =
        subOrder?.truckAssignment || null;
    }
    modalRef.componentInstance.mode = mode;

    modalRef.result
      .then((result) => {
        if (result === 'saved') {
          console.log('estimate added!');
        }
      })
      .catch(() => {
        console.log('errror');
      });
  }

  openAssignDriverModal(
    mode: 'create' | 'edit' | 'view' = 'create',
    subOrder: any
  ) {
    const modalRef = this.modalService.open(AssignDriverModalComponent, {
      centered: true,
      size: 'lg', // options: sm, lg, xl
      backdrop: 'static', // optional: prevent closing by click outside
    });

    modalRef.componentInstance.orderData = this.orderData;
    modalRef.componentInstance.subOrder = subOrder;
    if (mode === 'edit' || mode === 'view') {
      modalRef.componentInstance.initialData =
        subOrder?.truckAssignment || null;
    }
    modalRef.componentInstance.mode = mode;

    modalRef.result
      .then((result) => {
        if (result === 'saved') {
          console.log('estimate added!');
        }
      })
      .catch(() => {
        console.log('errror');
      });
  }

  openVehicleAssignModal() {
    const modalRef = this.modalService.open(VehicleAssignModalComponent, {
      centered: true,
      size: 'lg', // options: sm, lg, xl
      backdrop: 'static', // optional: prevent closing by click outside
    });

    modalRef.result
      .then((result) => {
        if (result === 'saved') {
          console.log('estimate added!');
        }
      })
      .catch(() => {
        console.log('errror');
      });
  }

  openReadyToDispatchModal(
    item: any,
    mode: 'create' | 'edit' | 'view' = 'create'
  ) {
    const modalRef = this.modalService.open(ReadyDispatchModalComponent, {
      centered: true,
      size: 'xl', // options: sm, lg, xl
      backdrop: 'static', // optional: prevent closing by click outside
    });

    modalRef.componentInstance.productDifferences =
      item?.productDifferences || {};
    modalRef.componentInstance.mode = mode;
    modalRef.componentInstance.orderData = this.orderData;
    modalRef.componentInstance.subOrder = item;
    modalRef.componentInstance.config = this.config;
    modalRef.componentInstance.initialData = item?.productDifferences || null;

    modalRef.result
      .then((result) => {
        if (result === 'saved') {
          console.log('estimate added!');
        }
      })
      .catch(() => {
        console.log('errror');
      });
  }

  getStatusDate(items: any, targetStatus: number) {
    const selected = items.find(
      (item: any) => item?.statusNumber === targetStatus
    );
    const test = selected ? selected?.at : null;
    return test;
  }

  openDeliverModal(item: any, mode: 'create' | 'edit' | 'view' = 'create') {
    const modalRef = this.modalService.open(DeliverModalComponent, {
      centered: true,
      size: 'xl', // options: sm, lg, xl
      backdrop: 'static', // optional: prevent closing by click outside
    });
    modalRef.componentInstance.productDifferences =
      item?.productDifferences || {};
    modalRef.componentInstance.mode = mode;
    modalRef.componentInstance.orderData = this.orderData;
    modalRef.componentInstance.subOrder = item;
    modalRef.componentInstance.initialData = item?.productDifferences || null;

    modalRef.result
      .then((result) => {
        if (result === 'saved') {
          console.log('estimate added!');
        }
      })
      .catch(() => {
        console.log('errror');
      });
  }

  openConfirmPaymentModal() {
    const modalRef = this.modalService.open(ConfirmPaymentModalComponent, {
      centered: true,
      size: 'lg', // options: sm, lg, xl
      backdrop: 'static', // optional: prevent closing by click outside
    });
    console.log('this.orderData::::', this.orderData);
    modalRef.componentInstance.orderData = this.orderData;
    modalRef.result
      .then((result) => {
        if (result === 'saved') {
          console.log('estimate added!');
        }
      })
      .catch(() => {
        console.log('errror');
      });
  }

  get allPickupItems() {
    return (
      this.orderData?.subOrders?.flatMap((so: any) => so.pickup?.items || []) ||
      []
    );
  }

  get partLoadtotalWeight() {
    return this.allPickupItems.reduce(
      (sum: number, i: any) => sum + (i.planned?.weight || 0),
      0
    );
  }

  get partLoadtotalQty() {
    return this.allPickupItems.reduce(
      (sum: number, i: any) => sum + (i.planned?.qty || 0),
      0
    );
  }
  ngOnDestroy() {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }
}
